import GroupView from './GroupView';
import getAriaLabelForFileProviderItem from '../utils/getAriaLabelForFileProviderItem';
import { observer } from 'mobx-react';
import { FocusZone } from 'office-ui-fabric-react/lib/FocusZone';
import {
    ISelection,
    Selection,
    SelectionMode,
    SelectionZone,
} from 'office-ui-fabric-react/lib/Selection';
import { formatUserDate } from 'owa-datetime';
import FileProviderItemViewState, {
    GroupCategory,
    GroupItemViewState,
} from '../../store/schema/FileProviderItemViewState';
import * as React from 'react';
import { IObjectWithKey } from 'office-ui-fabric-react/lib';
import strings from 'owa-strings/owa-mail-strings';

const styles = require('./FilePickerGroupsView.scss');

export interface FilePickerGroupsViewProps {
    dataSourceId: string;
    items: FileProviderItemViewState[];
    selectedItems: FileProviderItemViewState[];
    onLoadMoreRows: () => void;
    isLoadRowsInProgress: boolean;
    getCanLoadMore: () => boolean;
    onItemSelected?: (item: IObjectWithKey) => void;
    onItemsSelectionUpdated?: (selectedItems: FileProviderItemViewState[]) => void;
}

export interface FilePickerGroupsViewState {
    // The selection model is used to track the selection state of the files in the file picker
    selection: ISelection;
}

@observer
export default class FilePickerGroupView extends React.Component<
    FilePickerGroupsViewProps,
    FilePickerGroupsViewState
> {
    // Variable that keeps track of whether the component is mounted or not
    private hasMounted: boolean;

    constructor(props: FilePickerGroupsViewProps) {
        super(props);

        this.state = {
            selection: new Selection({
                onSelectionChanged: this.onItemsSelectionChanged,
                getKey: this.getKeyForItem,
            }),
        };
    }

    render() {
        const favoriteItemIds = this.props.items
            .filter(item => (item as GroupItemViewState).groupCategory == GroupCategory.Favorite)
            .map(item => item.key);
        const allItemIds = this.props.items.map(item => item.key);

        const showFavorites = favoriteItemIds.length > 0;
        const showAllGroups = allItemIds.length > 0;

        return (
            <div className={styles.outer}>
                {showFavorites && (
                    <div className={styles.container}>
                        <div className={styles.subHeader}>{strings.favoritesHeaderText}</div>
                        <FocusZone className={styles.focusZone}>
                            <SelectionZone
                                selection={this.state.selection}
                                selectionMode={SelectionMode.single}
                                onItemInvoked={item => this.props.onItemSelected(item)}>
                                <div>
                                    {favoriteItemIds.map((id, index) =>
                                        this.createItemView(id, index)
                                    )}
                                </div>
                            </SelectionZone>
                        </FocusZone>
                    </div>
                )}
                {showAllGroups && (
                    <div className={styles.container}>
                        <div className={styles.subHeader}>{strings.AllGroupsHeader}</div>
                        <FocusZone className={styles.focusZone}>
                            <SelectionZone
                                selection={this.state.selection}
                                selectionMode={SelectionMode.single}
                                onItemInvoked={item => this.props.onItemSelected(item)}>
                                <div>
                                    {allItemIds.map((id, index) => this.createItemView(id, index))}
                                </div>
                            </SelectionZone>
                        </FocusZone>
                    </div>
                )}
            </div>
        );
    }

    componentDidMount() {
        // When the component mounts we need to set the items and the selected items
        // so that if the user switches between the view the selection is still maintained
        this.state.selection.setItems(this.props.items, true);
        this.resetSelectedItems(this.props.selectedItems, this.state.selection);
        this.hasMounted = true;
    }

    componentWillReceiveProps(nextProps: FilePickerGroupsViewProps) {
        // We need to update the items in the selection when we load a new set of items, but
        // we don't want to reset it when we load more items (pagination).
        // This check prevents reseting the selection on pagination, while still keeping the selection up to date during folder navigation.
        // This also works (clears the state) if nextProps does not have items, as nextProps.items[0] will be undefined.
        if (this.props.items[0] !== nextProps.items[0]) {
            this.state.selection.setItems(nextProps.items, true /* shouldClear */);
        }

        // If something has changed in selected items then we reset the selection with the new selected items
        const shouldResetSelectedItems =
            nextProps.selectedItems.some(
                nextSelectedItem => this.props.selectedItems.indexOf(nextSelectedItem) === -1
            ) || nextProps.selectedItems.length === 0;
        if (shouldResetSelectedItems) {
            this.resetSelectedItems(nextProps.selectedItems, this.state.selection);
        }
    }

    componentWillUnmount() {
        this.hasMounted = false;
    }

    /* Called when the user selects items in the file picker */
    private onItemsSelectionChanged = () => {
        const selectedItems = this.state.selection.getSelection() as FileProviderItemViewState[];

        if (this.props.onItemsSelectionUpdated) {
            this.props.onItemsSelectionUpdated(selectedItems);
        }

        // Need to force update as the selection has changed if the component is mounted
        if (this.hasMounted) {
            this.forceUpdate();
        }
    };

    /* Use to create a key corresponding to an item when it is selected */
    private getKeyForItem = (item: any, index: number): string => {
        return index.toString();
    };

    private resetSelectedItems = (
        selectedItems: FileProviderItemViewState[],
        selection: ISelection
    ) => {
        // We need to disable the trigger of change event so during the process of resetting the selection
        selection.setChangeEvents(false);

        // Setting all items to unselected state
        selection.setAllSelected(false);

        // Setting the anchor to the first item
        this.state.selection.setIndexSelected(0, false /* isSelected */, true /* shouldAnchor */);

        // Getting the indexes of selected items and then setting selection to true for them
        selectedItems
            .map(selectedItem => this.props.items.indexOf(selectedItem))
            .forEach(selectedItemIndex =>
                selection.setIndexSelected(
                    selectedItemIndex,
                    true /* isSelected */,
                    false /* shouldAnchor */
                )
            );

        // We enable the change events when we are done
        selection.setChangeEvents(true);
    };

    private createItemView = (itemId: string, itemIndex: number): JSX.Element => {
        const item = this.props.items[itemIndex] as GroupItemViewState;
        const isSelected = this.state.selection.isIndexSelected(itemIndex);

        const itemAriaLabel = getAriaLabelForFileProviderItem(item);
        const secondaryText = this.getSecondaryText(item);

        return (
            <GroupView
                key={itemId}
                itemIndex={itemIndex}
                isSelected={isSelected}
                primaryText={item.name}
                secondaryText={secondaryText}
                ariaLabel={itemAriaLabel}
                groupSmtpEmailAddress={item.pagingMetadata.GroupSmtpAddress}
            />
        );

        throw new Error('Unexpected item type');
    };

    private getSecondaryText(item: GroupItemViewState): string {
        // LOC_BUG
        return item.joinDate ? 'Joined ' + formatUserDate(item.joinDate) : null;
    }
}
